/**
 * Print-specific CSS styles for PDF export.
 * Injected into off-screen container for html2pdf rendering.
 */
export const PDF_PRINT_STYLES = `
  /* Print-specific overrides for html2pdf */
  .print-container {
    font-family: Cambria, 'Times New Roman', Times, serif;
    font-size: 10pt;
    line-height: 1.2;
    color: #000;
    background: #fff;
    width: 180mm; /* A4 width minus margins */
    padding: 0;
    box-sizing: border-box;
  }

  .print-container * {
    box-sizing: border-box;
  }

  /* Paragraph spacing - prevent overlaps */
  .print-container p {
    margin: 5pt 0;
    orphans: 3;
    widows: 3;
  }

  /* Ensure tables render properly with proper spacing */
  .print-container table {
    border-collapse: collapse;
    width: 100%;
    page-break-inside: avoid;
    margin-top: 6px;
    margin-bottom: 16px;
  }

  .print-container th,
  .print-container td {
    border: 1px solid #000;
    padding: 2px 12px 22px;
  }

  /* Patient info table - no borders, compact layout */
  .print-container [data-section-type="patient-info"] table {
    margin: 0;
  }

  .print-container [data-section-type="patient-info"] td {
    border: none;
    padding: 2px 8px;
    vertical-align: top;
  }

  .print-container [data-section-type="patient-info"] p {
    margin: 0;
  }

  /* Headings with proper spacing */
  .print-container h1,
  .print-container h2,
  .print-container h3 {
    margin-top: 0;
    margin-bottom: 4px;
    page-break-after: avoid;
  }

  /* Section styling with proper spacing */
  .print-container [data-section-type] {
    margin-bottom: 16px;
  }

  .print-container [data-section-type="header"] {
    border: 2px solid #000;
    padding: 16px;
    margin-bottom: 8px !important;
    background: #fff;
  }
  
  .print-container [data-section-type="header"] p {
    margin: 2pt 0;
  }

  /* Add vertical padding to the text columns inside the header */
  .print-container [data-section-type="header"] > div {
    padding-bottom: 12px !important;
  }

  /* Patient info box - compact styling, no border */
  .print-container [data-section-type="patient-info"] {
    margin-top: 10px;
    margin-bottom: 16px;
    font-size: 9pt !important;
    page-break-inside: avoid;
  }

  .print-container [data-section-type="signatures"] {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid #ccc;
    page-break-inside: avoid;
  }

  /* Footer - strip the large top spacing so the captured image is compact.
     The margin-top and padding-top from the inline style are overridden here
     because the footer is captured as a small overlay image and the extra
     whitespace would inflate its height unnecessarily. */
  .print-container [data-section-type="footer"] {
    padding-top: 0 !important;
    padding-bottom: 6px !important;
  }

  /* Footer paragraphs - keep lines tight, override the generic 12pt bottom margin */
  .print-container [data-section-type="footer"] p {
    margin: 0 !important;
  }

  /* Results section spacing */
  .print-container [data-section-type="results"] {
    border-top: 1px solid black;
    padding-top: 0;
  }

  .print-container [data-section-type="results"] > *:first-child {
    margin-top: 0;
  }
  
  .print-container [data-section-type="results"] table {
    width: 90%;
    margin: 16px auto 16px;
  }

  /* Ensure content after section-type elements has proper spacing */
  .print-container [data-section-type] + p {
    margin-top: 16px;
  }

  /* Remove any selection or focus styles */
  .print-container ::selection {
    background: transparent;
  }

  .print-container *:focus {
    outline: none;
  }

  /* Ensure non-editable sections don't have background */
  .print-container [data-editable="false"] {
    background-color: transparent !important;
  }

  /* Lists - using custom bullets with better control */
  .print-container ul {
    list-style-type: none;
    padding-left: 0;
    margin: 0 0 0 12pt;
  }

  .print-container ol {
    list-style-type: none;
    padding-left: 0;
    margin: 0 0 0 12pt;
    counter-reset: item;
  }

  .print-container li {
    display: flex;
    align-items: flex-start;
    margin: 0 0 2pt 0;
    padding: 0;
    line-height: 1.2;
  }

  /* Create custom bullets using ::before pseudo-element */
  .print-container ul > li::before {
    content: "•";
    line-height: 1.2;
    margin-right: 6pt;
    flex-shrink: 0;
    width: 12pt;
  }

  .print-container ol > li::before {
    counter-increment: item;
    content: counter(item) ".";
    line-height: 1.2;
    margin-right: 6pt;
    flex-shrink: 0;
    min-width: 18pt;
  }

  /* Paragraphs inside list items take remaining space */
  .print-container li p {
    margin: 0;
    padding: 0;
    line-height: 1.2;
    flex: 1;
  }

  .print-container ul ul,
  .print-container ol ul {
    list-style-type: circle;
  }

  .print-container ul ul ul,
  .print-container ol ul ul {
    list-style-type: square;
  }

  .print-container ol ol {
    list-style-type: lower-alpha;
  }
`;
